// ------------------------------------------------------------
// DOCX IMPORTS
// ------------------------------------------------------------
import {
    AlignmentType,
    Document,
    Header,
    Footer,
    ImageRun,
    Packer,
    PageNumber,
    Paragraph,
    SectionType,
    Table,
    TableCell,
    TableRow,
    TextRun,
    HeadingLevel,
    WidthType,
} from "docx";

// ------------------------------------------------------------
// MAIN EXPORT FUNCTION
// ------------------------------------------------------------
export async function exportCertificateAndContractDocx({
    certificateClone,
    maturityRecord,
    idNumber,
    logoBuffer,
    sealBuffer,
    sealBuffer2,
    snapshot,
}) {
    const version = snapshot.version || "void";
    const template = window.contractTemplates?.[version];
    const templateVector = template?.vector || [];

    const doc = new Document({
        sections: [
            // SECTION 1: CERTIFICATE + MATURITY
            {
                properties: {
                    type: SectionType.CONTINUOUS,
                    page: {
                        margin: {
                            top: inchesToTwips(1),
                            bottom: inchesToTwips(1),
                            left: inchesToTwips(1),
                            right: inchesToTwips(1),
                        },
                    },
                },
                headers: { default: buildHeader(idNumber) },
                footers: { default: buildFooter(idNumber) },
                children: [
                    ...buildCertificateSection(certificateClone, logoBuffer, sealBuffer),
                    buildMaturityTable(maturityRecord),
                ],
            },

            // SECTION 2: CONTRACT (ALWAYS NEW PAGE)
            {
                properties: {
                    type: SectionType.NEXT_PAGE,
                    page: {
                        margin: {
                            top: inchesToTwips(1),
                            bottom: inchesToTwips(1),
                            left: inchesToTwips(1),
                            right: inchesToTwips(1),
                        },
                    },
                },
                headers: { default: buildHeader(idNumber) },
                footers: { default: buildFooter(idNumber) },
                children: [
                    buildContractLogo(logoBuffer),
                    ...buildContractDocxFromTemplateVector(snapshot, templateVector),
                    ...(sealBuffer2
                        ? [
                              new Paragraph({
                                  alignment: AlignmentType.RIGHT,
                                  children: [
                                      new ImageRun({
                                          data: sealBuffer2,
                                          transformation: {
                                              width: 288.0,
                                              height: 259.2,
                                          },
                                      }),
                                  ],
                              }),
                          ]
                        : []),
                ],
            },
        ],
    });

    const blob = await Packer.toBlob(doc);
    const fileName = `JP Levesque Builders_${idNumber}.docx`;

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

// ------------------------------------------------------------
// HEADER / FOOTER
// ------------------------------------------------------------
function buildHeader(idNumber) {
    return new Header({
        children: [
            new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                    new TextRun({
                        text: `Pro Forma Capital Note Certificate and Contract | Note ID ${idNumber}`,
                        font: "Arial",
                        size: 20,
                    }),
                    new TextRun({
                        text: "     ",
                        font: "Arial",
                        size: 20,
                    }),
                    /*
                    new TextRun({
                        children: ["Page ", PageNumber.CURRENT],
                        font: "Arial",
                        size: 20,
                    }),
                    */
                ],
            }),
        ],
    });
}

function buildFooter() {
    return new Footer({
        children: [
            new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                    new TextRun({
                        text: "Page ",
                        font: "Arial",
                        size: 20,
                    }),
                    new TextRun({
                        children: [PageNumber.CURRENT],
                        font: "Arial",
                        size: 20,
                    }),
                ],
            }),
        ],
    });
}

function cleanCurrency(value) {
    if (value == null) return "";

    // If it's an array, clean the first element
    if (Array.isArray(value)) {
        return cleanCurrency(value[0]);
    }

    const str = String(value).trim();

    // Only clean values that contain a dollar sign
    if (!str.includes("$")) {
        return str; // return untouched
    }

    // Extract numeric portion
    const numeric = str.replace(/[^0-9.\-]/g, "");
    const num = Number(numeric);

    if (isNaN(num)) return str;

    // ⭐ Round to nearest dollar
    const rounded = Math.round(num);

    // Format with commas, no decimals
    const whole = rounded.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    // Re-add the dollar sign
    return "$" + whole;
}

// ------------------------------------------------------------
// CERTIFICATE SECTION
// ------------------------------------------------------------
function buildCertificateSection(certificateClone, logoBuffer, sealBuffer) {
    const children = [];

    // Logo at top of certificate
    if (logoBuffer) {
        children.push(
            new Paragraph({
                spacing: { after: 200 },
                alignment: AlignmentType.CENTER,
                children: [
                    new ImageRun({
                        data: logoBuffer,
                        transformation: {
                            // width: 615.86,
                            width: 624.0,
                            // height: 58.4496,
                            height: 59.22,
                        }                        
                    }),
                ],
            })
        );
    }

    // Date
    const dateNode = certificateClone.querySelector("#today105");
    if (dateNode?.textContent.trim()) {
        children.push(
            new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                    new TextRun({
                        text: dateNode.textContent.trim(),
                        font: "Arial",
                        size: 24,
                    }),
                ],
                spacing: { after: 120 },
            })
        );
    }

    // Title
    const headingNode = certificateClone.querySelector("#heading_w");
    if (headingNode?.textContent.trim()) {
        children.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: headingNode.textContent.trim(),
                        font: "Arial",
                        size: 24,
                        bold: true,
                    }),
                ],
                heading: HeadingLevel.TITLE,
                alignment: AlignmentType.CENTER,
                spacing: { after: 200 },
            })
        );
    }

    // Certificate field table
    const noteDocTable = certificateClone.querySelector("#noteDocTable");
    if (noteDocTable) {
        children.push(buildCertificateFieldTable(noteDocTable));
    }

    // Padding under table
    children.push(
        new Paragraph({
            children: [new TextRun({ text: "", font: "Arial", size: 24 })],
            spacing: { after: 200 },
        })
    );

    // Summary 1
    const summary1 = certificateClone.querySelector("#summary1 p");
    if (summary1) {
        children.push(paragraphFromHtmlText(summary1.innerText));
    }

    // Summary + Seal
    const summary = certificateClone.querySelector("#summaryText p");
    if (summary) {
        children.push(...buildSummaryWithSeal(summary.innerText, sealBuffer));
    }

    return children;
}

// ------------------------------------------------------------
// CERTIFICATE FIELD TABLE
// ------------------------------------------------------------
function buildCertificateFieldTable(tableNode) {
    const rows = [];

    tableNode.querySelectorAll("tr").forEach(tr => {
        const th = tr.querySelector("th");
        const td = tr.querySelector("td");
        if (!th || !td) return;

        rows.push(
            new TableRow({
                children: [
                    new TableCell({
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: th.textContent.trim(),
                                        bold: true,
                                        font: "Arial",
                                        size: 24,
                                    }),
                                ],
                            }),
                        ],
                        margins: {
                            top: inchesToTwips(0.05),
                            bottom: inchesToTwips(0.05),
                            left: inchesToTwips(0.25),
                            right: inchesToTwips(0.25),
                        },
                    }),
                    new TableCell({
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: td.textContent.trim(),
                                        font: "Arial",
                                        size: 24,
                                    }),
                                ],
                            }),
                        ],
                        margins: {
                            top: inchesToTwips(0.05),
                            bottom: inchesToTwips(0.05),
                            left: inchesToTwips(0.25),
                            right: inchesToTwips(0.25),
                        },
                    }),
                ],
            })
        );
    });

    return new Table({
        width: { size: inchesToTwips(5.5), type: WidthType.DXA },
        alignment: AlignmentType.CENTER,
        rows,
    });
}

// ------------------------------------------------------------
// SUMMARY + SEAL
// ------------------------------------------------------------
function buildSummaryWithSeal(text, sealBuffer) {
    if (!sealBuffer) {
        return [paragraphFromHtmlText(text)];
    }

    const summaryCell = new TableCell({
        children: [paragraphFromHtmlText(text)],
        width: { size: 80, type: WidthType.PERCENTAGE },
        borders: {
            top: { size: 0, color: "FFFFFF" },
            bottom: { size: 0, color: "FFFFFF" },
            left: { size: 0, color: "FFFFFF" },
            right: { size: 0, color: "FFFFFF" },
        },        
        margins: {
            top: 0,
            bottom: inchesToTwips(0.05),
            left: 0,
            right: inchesToTwips(0.03),   // ≈ 3px right padding
        },
        shading: { fill: "FFFFFF" },
    });

    const sealCell = new TableCell({
        children: [
            new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [
                    new ImageRun({
                        data: sealBuffer,
                        transformation: {
                            width: 166.25,
                            height: 149.15
                        },
                    }),
                ],
            }),
        ],
        verticalAlign: "bottom",
        width: { size: 20, type: WidthType.PERCENTAGE },
        borders: {
            top: { size: 0, color: "FFFFFF" },
            bottom: { size: 0, color: "FFFFFF" },
            left: { size: 0, color: "FFFFFF" },
            right: { size: 0, color: "FFFFFF" },
        },        
        margins: {
            top: 0,
            bottom: inchesToTwips(0.05),
            left: inchesToTwips(0.03),    // ≈ 3px left padding
            right: 0,
        },
        shading: { fill: "FFFFFF" },
    });

    const table = new Table({
        width: { size: inchesToTwips(6.5), type: WidthType.DXA },
        rows: [
            new TableRow({
                children: [summaryCell, sealCell],
            }),
        ],
        borders: {
            top: { size: 0, color: "FFFFFF" },
            bottom: { size: 0, color: "FFFFFF" },
            left: { size: 0, color: "FFFFFF" },
            right: { size: 0, color: "FFFFFF" },
    
            // ⭐ These two remove the vertical line between cells
            insideVertical: { size: 0, color: "FFFFFF" },
            insideHorizontal: { size: 0, color: "FFFFFF" },
        },
        shading: { fill: "FFFFFF" },
        tableLook: {
            firstRow: false,
            lastRow: false,
            firstColumn: false,
            lastColumn: false,
            noHBand: true,
            noVBand: true,
        },
    });
    
    return [
        table,
        new Paragraph({
            spacing: { after: 100 }, // ~5px before maturity table
            children: [],
        }),
    ];
}

// ------------------------------------------------------------
// MATURITY TABLE
// ------------------------------------------------------------
function buildMaturityTable(record) {
    if (!record) return new Paragraph("");

    const firstKeys = [
        "Timestamp", "Status", "investmentAmount", "interestRate",
        "Term", "maturityDate", "earnedInterest",
        "contractCode", "Value", "Payment",
    ];

    const keys = [
        "oeTimestamp", "newTermStatus", "newAmount", "newRate",
        "newTerm", "newMaturity", "newROI",
        "newRepaymentOptions", "newValue", "newPayment",
    ];

    const headings = [
        "NOTE ID", "STATUS", "AMOUNT", "RATE", "TERM",
        "MATURITY", "ROI", "OPTIONS",
        "VALUE", "PAYMENT",
    ];

    // Preprocess headings so "Maturity Payment" doesn't split

const headerRow = new TableRow({
    children: headings.map(h =>
        new TableCell({
            children: [
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    children: [
                        new TextRun({
                            text: h,
                            bold: true,
                            font: "Arial",
                            size: 14,
                        }),
                    ],
                    spacing: { before: 0, after: 0 },
                }),
            ],
            verticalAlign: "center",
            margins: { top: 50, bottom: 50, left: 50, right: 50 },
        })
    ),
});

const firstRow = new TableRow({
    children: firstKeys.map(key =>
        new TableCell({
            children: [
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    children: [
                        new TextRun({
                            text: cleanCurrency(record[key]),
                            font: "Arial",
                            size: 14,
                        }),
                    ],
                    spacing: { before: 0, after: 0 },
                }),
            ],
            verticalAlign: "center",
            margins: { top: 50, bottom: 50, left: 50, right: 50 },
        })
    ),
});

const renewalRows = [];
const rowCount = record.newMaturity?.length || 0;

for (let i = 0; i < rowCount; i++) {
    renewalRows.push(
        new TableRow({
            children: keys.map(key =>
                new TableCell({
                    children: [
                        new Paragraph({
                            alignment: AlignmentType.CENTER,
                            children: [
                                new TextRun({
                                    text: cleanCurrency(record[key]?.[i]),
                                    font: "Arial",
                                    size: 14,
                                }),
                            ],
                            spacing: { before: 0, after: 0 },
                        }),
                    ],
                    verticalAlign: "center",
                    margins: { top: 50, bottom: 50, left: 50, right: 50 },
                })
            ),
        })
    );
}

return new Table({
    width: { size: inchesToTwips(6.5), type: WidthType.DXA },
    layout: "fixed",
    rows: [headerRow, firstRow, ...renewalRows],
});
}
// ------------------------------------------------------------
// CONTRACT SECTION
// ------------------------------------------------------------
function buildContractLogo(logoBuffer) {
    if (!logoBuffer) return new Paragraph("");

    return new Paragraph({
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
        children: [
            new ImageRun({
                data: logoBuffer,
                transformation: {
                    // width: 615.86,
                    width: 624.0,
                    // height: 58.4496,
                    height: 59.22,
                },
            }),
        ],
    });
}

function buildContractDocxFromTemplateVector(snapshot, vector) {
    const paragraphs = [];
    const checkboxes = snapshot.checkboxes || {};
    const inputs = snapshot.inputs || {};
    const dynamicText = snapshot.dynamicText || {};
    const revealed = snapshot.revealedMessage || "";
    for (const node of vector) {
        switch (node.type) {
            case "paragraph": {
                const text = node.text?.trim() || "";
                if (isCongratsParagraph(text)) {
                    // if (text.trim().startsWith("Congratulations!")) {
                    if (text.trim === revealed) {
                        // console.log("paragraph text:", text)
                        // console.log("skipping congrat paragraph", s)
                        paragraphs.push(paragraphFromHtmlText(revealed, node.style));
                        break;
                    }
                }
                paragraphs.push(paragraphFromHtmlText(text, node.style));
                break;
            }
            
            case "title":
                paragraphs.push(
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: node.text,
                                font: "Arial",
                                size: 24,
                                bold: true,
                                color: "000000",
                            }),
                        ],
                        heading: HeadingLevel.HEADING_1,
                        alignment: node.style?.textAlign || undefined,
                        spacing: { after: 200 },
                    })
                );
                break;

            case "subtitle":
                paragraphs.push(
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: node.text,
                                italics: true,
                                font: "Arial",
                                size: 24,
                                color: "000000",
                            }),
                        ],
                        alignment: node.style?.textAlign || undefined,
                        spacing: { after: 120 },
                    })
                );
                break;

            case "checkbox": {
                const checked = !!checkboxes[node.id];
                paragraphs.push(buildCheckboxParagraph(node.label, checked));
                break;
            }

            case "input": {
                const value = inputs[node.id] || "";
                const label = node.label || "";
                paragraphs.push(
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: label,
                                bold: false,
                                font: "Arial",
                                size: 24,
                                color: "000000",
                            }),
                            new TextRun({
                                text: " ",
                                font: "Arial",
                                size: 24,
                                color: "000000",
                            }),
                            new TextRun({
                                text: value,
                                font: "Arial",
                                size: 24,
                                color: "000000",
                            }),
                        ],
                        spacing: { after: 120 },
                    })
                );
                break;
            }

            case "dynamicText": {
                const value = dynamicText[node.id] || "";
                if (value) {
                    paragraphs.push(
                        new Paragraph({
                            children: [
                                new TextRun({
                                    text: value,
                                    font: "Arial",
                                    size: 24,
                                    color: "000000",
                                }),
                            ],
                            spacing: { after: 120 },
                        })
                    );
                }
                break;
            }
            /*
            case "revealedMessageTemplate": {
                if (!revealed) break;
                paragraphs.push(
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: revealed,
                                font: "Arial",
                                size: 24,
                                color: "000000",
                            }),
                        ],
                        spacing: { after: 200 },
                    })
                );
                break;
            }
            */
            case "image": {
                if (!node.src) { break; }
                paragraphs.push(
                    new Paragraph({
                        alignment: AlignmentType.CENTER,
                        children: [
                            new TextRun(snapshot.images[node.id]?.filename || ""),
                        ],
                    })
                );
                break;
            }
        } 
    }
    //console.log("paragraphs", paragraphs)
    return paragraphs;
}

// ------------------------------------------------------------
// HELPERS
// ------------------------------------------------------------
function paragraphFromHtmlText(text, style = {}) {
    if (!text) {
        console.log("paragraph is not text returning")
        return new Paragraph("");
    }

    let working = text
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/p>/gi, "\n");

    const segments = [];
    const regex = /(<\/?[^>]+>)/g;
    const parts = working.split(regex);

    let bold = false;
    let italics = false;
    let underline = false;

    for (let part of parts) {
        const lower = part.toLowerCase();

        if (lower === "<b>" || lower === "<strong>") {
            bold = true;
            continue;
        }
        if (lower === "</b>" || lower === "</strong>") {
            bold = false;
            continue;
        }
        if (lower === "<i>" || lower === "<em>") {
            italics = true;
            continue;
        }
        if (lower === "</i>" || lower === "</em>") {
            italics = false;
            continue;
        }
        if (lower === "<u>") {
            underline = true;
            continue;
        }
        if (lower === "</u>") {
            underline = false;
            continue;
        }

        if (lower.startsWith("<")) continue;

        const lines = part.split(/\n/);
        lines.forEach((line, idx) => {
            const trimmed = line.trim();
            if (trimmed.length > 0) {
                segments.push(
                    new TextRun({
                        text: trimmed + (idx < lines.length - 1 ? " " : ""),
                        bold,
                        italics,
                        underline,
                        font: "Arial",
                        size: 24,
                    })
                );
            }
        });
    }
    // return new Paragraph({children: segments,alignment: style.textAlign || "left",spacing: { after: 120 },});
    const newParagraph = new Paragraph({
        children: segments,
        alignment: style.textAlign || undefined,
        spacing: { after: 120 },
    });
    return deepFinalize(newParagraph);
}

function buildCheckboxParagraph(label, isChecked) {
    return new Paragraph({
        children: [
            new TextRun({
                text: isChecked ? "☑ " : "☐ ",
                bold: true,
                font: "Arial",
                size: 24,
            }),
            new TextRun({
                text: label || "",
                font: "Arial",
                size: 24,
            }),
        ],
        spacing: { after: 120 },
    });
}
function deepFinalize(node) {
    if (!node || typeof node !== "object") return node;
    // Force docx to resolve internal XML structures
    try { void node.root; } catch (_) {}
    try { void node.properties; } catch (_) {}
    // If node has children (Paragraph, TableRow, TableCell, etc.)
    if (Array.isArray(node.root)) {
        for (const child of node.root) {
            deepFinalize(child);
        }
    }
    // objects storing children in .children instead of .root
    if (Array.isArray(node.children)) {
        for (const child of node.children) {
            deepFinalize(child);
        }
    }
    return node;
}
function isCongratsParagraph(text) {
    if (!text) return false;
    
    const t = text.toLowerCase();
    return true;
    /*
    return t.includes("congratulations! you originated a waiting list placeholder") ||
           t.includes("congratulations! you`re on your way originating a waiting list placeholder");
           */
}

function inchesToTwips(inches) {
    return Math.round(inches * 1440);
}
